import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { BirthDetails, Gender, Language } from '../types/kundali';
import { X, Search, MapPin, Calendar, Clock, User, Check, Globe } from 'lucide-react';
import { useClickOutside } from '../hooks/useClickOutside';
import { getUserLocationPref, COMMON_TIMEZONES } from '../services/geoService';

interface BirthFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (details: BirthDetails) => void;
  initialDetails?: BirthDetails;
  lang?: Language;
}

interface GeocodingResult {
  display_name: string;
  name: string;
  latitude: number;
  longitude: number;
  country: string;
  timezoneOffsetMinutes: number;
}

const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const BirthFormModal: React.FC<BirthFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialDetails,
  lang = 'en'
}) => {
  const isHi = lang === 'hi';
  const isEditMode = Boolean(initialDetails);
  const [name, setName] = useState(initialDetails?.name || '');
  const [gender, setGender] = useState<Gender | ''>(initialDetails?.gender || '');
  const [date, setDate] = useState(initialDetails?.date || '');
  const [time, setTime] = useState(initialDetails?.time || '');
  
  // Date entry configurations
  const [dateInputMethod, setDateInputMethod] = useState<'calendar' | 'manual'>('manual');
  const [manualDay, setManualDay] = useState('');
  const [manualMonth, setManualMonth] = useState('');
  const [manualYear, setManualYear] = useState('');

  // Auto-advance input element refs
  const monthInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Location states
  const [searchQuery, setSearchQuery] = useState(initialDetails?.placeName || '');
  const [suggestions, setSuggestions] = useState<GeocodingResult[]>([]);

  // Global Outside-Click & Escape Dismissal for Search Suggestions (V5.15-R7)
  useClickOutside(suggestionsRef, suggestions.length > 0, () => setSuggestions([]));
  const [isSearching, setIsSearching] = useState(false);
  const defaultLoc = getUserLocationPref();
  const [selectedPlace, setSelectedPlace] = useState<{
    placeName: string;
    latitude: number;
    longitude: number;
    timezoneOffsetMinutes: number;
  }>({
    placeName: initialDetails?.placeName || defaultLoc.placeName || '',
    latitude: initialDetails?.latitude ?? defaultLoc.latitude ?? 28.6139,
    longitude: initialDetails?.longitude ?? defaultLoc.longitude ?? 77.2090,
    timezoneOffsetMinutes: initialDetails?.timezoneOffsetMinutes ?? defaultLoc.timezoneOffsetMinutes ?? 330
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  // Reset or initialize form when modal opens or initialDetails changes
  useEffect(() => {
    if (isOpen) {
      setName(initialDetails?.name || '');
      setGender(initialDetails?.gender || '');
      const defaultDate = initialDetails?.date || '';
      setDate(defaultDate);
      setTime(initialDetails?.time || '');
      setSearchQuery(initialDetails?.placeName || '');
      const def = getUserLocationPref();
      setSelectedPlace({
        placeName: initialDetails?.placeName || def.placeName || '',
        latitude: initialDetails?.latitude ?? def.latitude ?? 28.6139,
        longitude: initialDetails?.longitude ?? def.longitude ?? 77.2090,
        timezoneOffsetMinutes: initialDetails?.timezoneOffsetMinutes ?? def.timezoneOffsetMinutes ?? 330
      });
      setSuggestions([]);

      if (defaultDate) {
        const parts = defaultDate.split('-');
        if (parts.length === 3) {
          setManualYear(parts[0]);
          setManualMonth(parts[1]);
          setManualDay(parts[2]);
        }
      } else {
        setManualDay('');
        setManualMonth('');
        setManualYear('');
      }
    }
  }, [isOpen, initialDetails]);

  // Synchronize manual inputs with main date state
  useEffect(() => {
    if (dateInputMethod === 'manual') {
      const d = manualDay.padStart(2, '0');
      const m = manualMonth.padStart(2, '0');
      const y = manualYear;
      if (manualDay && manualMonth && manualYear && manualYear.length === 4) {
        setDate(`${y}-${m}-${d}`);
      } else {
        setDate('');
      }
    }
  }, [manualDay, manualMonth, manualYear, dateInputMethod]);

  const handleToggleInputMethod = (method: 'calendar' | 'manual') => {
    setDateInputMethod(method);
    if (method === 'manual' && date) {
      const parts = date.split('-');
      if (parts.length === 3) {
        setManualYear(parts[0]);
        setManualMonth(parts[1]);
        setManualDay(parts[2]);
      }
    }
  };

  const handleDayChange = (val: string) => {
    const clean = val.replace(/\D/g, '').slice(0, 2);
    if (clean !== '' && (parseInt(clean, 10) < 1 || parseInt(clean, 10) > 31)) {
      return;
    }
    setManualDay(clean);
    if (clean.length === 2 && monthInputRef.current) {
      monthInputRef.current.focus();
    }
  };

  const handleMonthChange = (val: string) => {
    const clean = val.replace(/\D/g, '').slice(0, 2);
    if (clean !== '' && (parseInt(clean, 10) < 1 || parseInt(clean, 10) > 12)) {
      return;
    }
    setManualMonth(clean);
    if (clean.length === 2 && yearInputRef.current) {
      yearInputRef.current.focus();
    }
  };

  const handleYearChange = (val: string) => {
    const clean = val.replace(/\D/g, '').slice(0, 4);
    setManualYear(clean);
  };

  const formatToDDMMMYYYY = (dateStr: string): string => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return '';
    const yyyy = parts[0];
    const mm = parseInt(parts[1], 10);
    const dd = parts[2];
    if (isNaN(mm) || mm < 1 || mm > 12) return '';
    const mmm = MONTH_NAMES_SHORT[mm - 1];
    return `${dd}/${mmm}/${yyyy}`;
  };

  // Debounced search for location
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 3) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await fetch(`/api/geocoding?q=${encodeURIComponent(searchQuery)}`);
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data);
        }
      } catch (e) {
        console.error('Geocoding search failed:', e);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!gender) return;
    onSubmit({
      name,
      gender,
      date,
      time,
      latitude: selectedPlace.latitude,
      longitude: selectedPlace.longitude,
      placeName: selectedPlace.placeName || searchQuery || 'Unknown Location',
      timezoneOffsetMinutes: selectedPlace.timezoneOffsetMinutes
    });
    onClose();
  };

  const selectSuggestion = (s: GeocodingResult) => {
    setSelectedPlace({
      placeName: s.display_name,
      latitude: s.latitude,
      longitude: s.longitude,
      timezoneOffsetMinutes: s.timezoneOffsetMinutes
    });
    setSearchQuery(s.name || s.display_name);
    setSuggestions([]);
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div 
        role="dialog"
        aria-modal="true"
        aria-labelledby="birth-form-title"
        className="bg-astro-bg border border-astro-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden text-astro-text"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 bg-astro-card border-b border-astro-border flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-astro-primary border border-astro-primary flex items-center justify-center text-astro-accent font-bold font-serif text-lg shadow-sm">
              ॐ
            </div>
            <div>
              <h2 id="birth-form-title" className="text-lg font-bold text-astro-primary font-serif">
                {isEditMode 
                  ? (isHi ? 'कुंडली विवरण संपादित करें' : 'Edit Kundali Details') 
                  : (isHi ? 'नई कुंडली' : 'New Kundali')}
              </h2>
              <p className="text-xs text-astro-secondary-text">
                {isEditMode
                  ? (isHi ? 'जन्म तिथि, समय या स्थान बदलें एवं समस्त चार्ट्स का पुनर्परिकलन करें' : 'Update DOB, time, or place to recalculate all charts')
                  : (isHi ? 'सटीक चार्ट गणना हेतु जन्म विवरण दर्ज करें' : 'Enter precise birth details for exact chart calculation')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={isHi ? 'बंद करें' : 'Close'}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center p-2 rounded-lg text-astro-secondary-text hover:text-astro-text hover:bg-astro-border/50 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          {/* Name & Gender */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2 space-y-1">
              <label className="text-xs font-semibold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-astro-primary" /> Full Name
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Aarav Sharma"
                className="w-full bg-astro-card border border-astro-border focus:border-astro-primary rounded-xl px-3.5 py-2 text-sm text-astro-text focus:outline-none transition-all placeholder-[#4B5956]/60"
              />
            </div>

            <div className="space-y-1">
              <label className="text-xs font-semibold text-astro-secondary-text uppercase tracking-wider">Gender</label>
              <select
                required
                value={gender}
                onChange={(e) => setGender(e.target.value as Gender)}
                className="w-full bg-astro-card border border-astro-border focus:border-astro-primary rounded-xl px-3 py-2 text-sm text-astro-text focus:outline-none"
              >
                <option value="">Select Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-astro-primary" /> Date of Birth
                </label>
                
                {/* Method selector options for both types */}
                <div className="flex bg-[#F1ECE1] rounded-lg p-0.5 text-[10px] font-medium border border-astro-border">
                  <button
                    type="button"
                    onClick={() => handleToggleInputMethod('manual')}
                    className={`px-2 py-0.5 rounded-md transition-all ${dateInputMethod === 'manual' ? 'bg-astro-primary text-white shadow-sm' : 'text-astro-secondary-text hover:text-astro-text'}`}
                  >
                    Manual
                  </button>
                  <button
                    type="button"
                    onClick={() => handleToggleInputMethod('calendar')}
                    className={`px-2 py-0.5 rounded-md transition-all ${dateInputMethod === 'calendar' ? 'bg-astro-primary text-white shadow-sm' : 'text-astro-secondary-text hover:text-astro-text'}`}
                  >
                    Calendar
                  </button>
                </div>
              </div>

              {dateInputMethod === 'manual' ? (
                /* Manual Inputs with strict digit restriction */
                <div className="flex items-center gap-1.5">
                  <div className="w-16">
                    <input
                      type="text"
                      required
                      placeholder="DD"
                      maxLength={2}
                      value={manualDay}
                      onChange={(e) => handleDayChange(e.target.value)}
                      className="w-full text-center bg-astro-card border border-astro-border focus:border-astro-primary rounded-xl px-2 py-2 text-sm text-astro-text font-semibold focus:outline-none placeholder-[#4B5956]/40"
                    />
                  </div>
                  <span className="text-astro-border font-bold">/</span>
                  <div className="w-16">
                    <input
                      ref={monthInputRef}
                      type="text"
                      required
                      placeholder="MM"
                      maxLength={2}
                      value={manualMonth}
                      onChange={(e) => handleMonthChange(e.target.value)}
                      className="w-full text-center bg-astro-card border border-astro-border focus:border-astro-primary rounded-xl px-2 py-2 text-sm text-astro-text font-semibold focus:outline-none placeholder-[#4B5956]/40"
                    />
                  </div>
                  <span className="text-astro-border font-bold">/</span>
                  <div className="flex-1">
                    <input
                      ref={yearInputRef}
                      type="text"
                      required
                      placeholder="YYYY"
                      maxLength={4}
                      value={manualYear}
                      onChange={(e) => handleYearChange(e.target.value)}
                      className="w-full text-center bg-astro-card border border-astro-border focus:border-astro-primary rounded-xl px-3 py-2 text-sm text-astro-text font-semibold focus:outline-none placeholder-[#4B5956]/40"
                    />
                  </div>
                </div>
              ) : (
                /* Calendar Picker Input */
                <input
                  type="date"
                  required
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full bg-astro-card border border-astro-border focus:border-astro-primary rounded-xl px-3.5 py-2 text-sm text-astro-text focus:outline-none"
                />
              )}

              {/* Show beautifully parsed formatted output after input as dd/mmm/yyyy */}
              {date ? (
                <div className="mt-1 text-[11px] text-astro-primary font-semibold bg-[#F1ECE1] border border-astro-border px-2.5 py-1 rounded-lg inline-flex items-center gap-1 animate-fade-in">
                  <Check className="w-3.5 h-3.5 text-emerald-700" />
                  Format: {formatToDDMMMYYYY(date)}
                </div>
              ) : (
                <div className="mt-1 text-[10px] text-astro-secondary-text/60 italic">
                  Please enter a valid day, month, and 4-digit year.
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-astro-primary" /> Time of Birth
              </label>
              <input
                type="time"
                step="1"
                required
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full bg-astro-card border border-astro-border focus:border-astro-primary rounded-xl px-3.5 py-2 text-sm text-astro-text focus:outline-none"
              />
            </div>
          </div>

          {/* Place of Birth with Search */}
          <div className="space-y-1 relative" ref={suggestionsRef}>
            <label className="text-xs font-semibold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-astro-primary" /> Place of Birth (City / Town)
            </label>
            <div className="relative">
              <input
                type="text"
                required
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type city e.g. Mumbai, New York, London..."
                className="w-full bg-astro-card border border-astro-border focus:border-astro-primary rounded-xl pl-9 pr-8 py-2 text-sm text-astro-text focus:outline-none placeholder-[#4B5956]/60"
              />
              <Search className="w-4 h-4 text-astro-secondary-text absolute left-3 top-2.5" />
              {isSearching && (
                <div className="w-4 h-4 border-2 border-astro-primary border-t-transparent rounded-full animate-spin absolute right-3 top-2.5" />
              )}
            </div>

            {/* Suggestions Dropdown */}
            {suggestions.length > 0 && (
              <div className="absolute left-0 right-0 mt-1 bg-astro-bg border border-astro-border rounded-xl shadow-2xl z-50 max-h-48 overflow-y-auto divide-y divide-astro-border">
                {suggestions.map((s, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => selectSuggestion(s)}
                    className="w-full text-left px-3.5 py-2.5 text-xs hover:bg-astro-card hover:text-astro-primary transition-colors flex items-start gap-2"
                  >
                    <MapPin className="w-3.5 h-3.5 text-astro-primary mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold text-astro-text">{s.name}</div>
                      <div className="text-[10px] text-astro-secondary-text line-clamp-1">{s.display_name}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Location Summary Badge */}
          <div className="p-3 bg-astro-card rounded-xl border border-astro-border text-xs flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-astro-primary" />
              <div>
                <span className="font-medium text-astro-secondary-text">Coords:</span>{' '}
                <span className="text-astro-primary font-semibold">
                  {selectedPlace.latitude.toFixed(4)}° N, {selectedPlace.longitude.toFixed(4)}° E
                </span>
              </div>
            </div>
            <div className="text-astro-secondary-text text-[11px]">
              UTC Offset: <span className="font-mono text-astro-text font-semibold">{(selectedPlace.timezoneOffsetMinutes / 60).toFixed(1)} hrs</span>
            </div>
          </div>

          {/* Timezone Selection */}
          <div className="space-y-1">
            <label className="text-xs font-semibold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-astro-primary" /> Timezone (Default from Settings Menu)
            </label>
            <select
              value={selectedPlace.timezoneOffsetMinutes}
              onChange={(e) => setSelectedPlace({ ...selectedPlace, timezoneOffsetMinutes: parseInt(e.target.value, 10) || 330 })}
              className="w-full bg-astro-card border border-astro-border focus:border-astro-primary rounded-xl px-3.5 py-2 text-xs sm:text-sm text-astro-text focus:outline-none cursor-pointer"
            >
              {COMMON_TIMEZONES.map((tz, idx) => (
                <option key={idx} value={tz.offsetMinutes}>
                  {tz.label} — {tz.representativeRegions}
                </option>
              ))}
            </select>
          </div>

          {/* Toggle Advanced Manual Coordinates */}
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-xs text-astro-primary hover:underline flex items-center gap-1 font-semibold"
            >
              {showAdvanced ? 'Hide manual coordinate fields' : 'Edit latitude, longitude & timezone manually'}
            </button>
          </div>

          {showAdvanced && (
            <div className="grid grid-cols-3 gap-2 p-3 bg-astro-card rounded-xl border border-astro-border text-xs">
              <div>
                <label className="text-[11px] text-astro-secondary-text">Latitude</label>
                <input
                  type="number"
                  step="0.0001"
                  value={selectedPlace.latitude}
                  onChange={(e) => setSelectedPlace({ ...selectedPlace, latitude: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-astro-bg border border-astro-border rounded-lg p-1.5 text-astro-text"
                />
              </div>
              <div>
                <label className="text-[11px] text-astro-secondary-text">Longitude</label>
                <input
                  type="number"
                  step="0.0001"
                  value={selectedPlace.longitude}
                  onChange={(e) => setSelectedPlace({ ...selectedPlace, longitude: parseFloat(e.target.value) || 0 })}
                  className="w-full bg-astro-bg border border-astro-border rounded-lg p-1.5 text-astro-text"
                />
              </div>
              <div>
                <label className="text-[11px] text-astro-secondary-text">Timezone (min)</label>
                <input
                  type="number"
                  value={selectedPlace.timezoneOffsetMinutes}
                  onChange={(e) => setSelectedPlace({ ...selectedPlace, timezoneOffsetMinutes: parseInt(e.target.value) || 0 })}
                  className="w-full bg-astro-bg border border-astro-border rounded-lg p-1.5 text-astro-text"
                />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-3 border-t border-astro-border flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-astro-secondary-text hover:text-astro-text hover:bg-astro-card text-xs font-semibold cursor-pointer"
            >
              {isHi ? 'रद्द करें' : 'Cancel'}
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 rounded-xl bg-astro-primary hover:bg-[#123d3c] text-white font-bold text-sm shadow-lg shadow-astro-primary/20 transition-all flex items-center gap-1.5 cursor-pointer"
            >
              <Check className="w-4 h-4 text-astro-accent" />
              {isEditMode
                ? (isHi ? 'पुनर्परिकलन एवं चार्ट अपडेट करें' : 'Recalculate & Update Charts')
                : (isHi ? 'कुंडली बनाएं' : 'Generate Kundali')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};
